import { useEffect, useRef } from "react";
import type {
  PayrollStatementNotificationRecipient,
  PayrollStatementPackApproval,
  PayrollStatementPackApprovalsResponse,
} from "./types";

/** What the payload says about one technician's approval, as a value to compare. */
const answerSignature = (approval: PayrollStatementPackApproval | null) =>
  approval
    ? `${approval.approved_at}|${approval.ai_script_run_id}|${approval.current}`
    : "none";

/**
 * The latest comparison the page has actually been given, per technician.
 *
 * `mergePolledPackApprovals` keeps a known answer when a poll reports it could not
 * compare, and the answer it kept was the one the PAGE LOADED WITH — the frozen
 * payload is what every poll is merged against, so nothing a poll said in between
 * survived. That is wrong in the one sequence that matters: loaded `true`, a poll
 * reports `false` and the column warns, a later poll cannot compare, and the warning
 * is replaced by the tick from page load. A manager is told the pay is fine and the
 * count agrees with them.
 *
 * So the answers are remembered here as they arrive. Only comparisons a poll could
 * actually MAKE go in: an unknown is the absence of one, and recording it would
 * overwrite the thing this exists to keep.
 *
 * What supersedes them is a payload the server has just DELIVERED. That payload builds
 * the projection bundle rather than reading a warm one, so its comparison is the better
 * one and it is also the newer one, whatever it says — including when it repeats the
 * answer it gave before. Remembered answers are dropped wholesale when one lands.
 *
 * A delivery is a fetch that RETURNED DATA: one seen in flight, which settles having
 * moved `dataUpdatedAt` and not `errorUpdatedAt`. Both halves are load-bearing, and
 * each of them is a bug this had:
 *
 * - Watching the data instead of the fetch. The recipients array is rewritten by
 *   `useSavePayrollStatementNote` through `setQueryData` on every note save, with every
 *   answer in it unchanged — so a saved note undid a "Pay changed" a poll had reported.
 * - Comparing `dataUpdatedAt` against the last delivery rather than against the start
 *   of THIS flight. That same note save moves the timestamp without any fetch, so a
 *   later failed refresh settled on a value that differed from the last delivery and
 *   read as one.
 * - Ignoring `errorUpdatedAt`. A fetch that fails leaves the data where it was, but a
 *   note save landing WHILE it is in flight moves the timestamp under it, which would
 *   read as a delivery again. A failure stamps its own clock, so this can tell them
 *   apart.
 *
 * The per-technician check underneath is a backstop for a fetch that begins and settles
 * inside one render, which would leave the flight unobserved: a payload whose answer
 * about a technician has CHANGED has plainly measured again, whether or not we saw it
 * happen. It cannot fire on a note save, which changes no answer. And a payload that
 * has gone back to "cannot tell" supersedes nothing, for the same reason an unknown
 * poll does not.
 *
 * Read during the render rather than in an effect: an effect runs after the render
 * that would already have preferred the superseded answer, and nothing guarantees
 * another render to correct it.
 *
 * @returns technician id => the latest approval a poll could compare
 */
export const useKnownPackApprovals = ({
  recipients,
  polled,
  payloadFetching,
  payloadUpdatedAt,
  payloadErroredAt,
}: {
  recipients: PayrollStatementNotificationRecipient[] | undefined;
  polled: PayrollStatementPackApprovalsResponse | undefined;
  payloadFetching: boolean;
  payloadUpdatedAt: number;
  payloadErroredAt: number;
}): Map<string, PayrollStatementPackApproval> => {
  const known = useRef(new Map<string, PayrollStatementPackApproval>());
  const fromPayload = useRef(new Map<string, string>());
  const inFlight = useRef<{ data: number; error: number } | null>(null);

  if (payloadFetching) {
    // Where the clocks stood when THIS fetch began, so what it did is read off its own
    // flight rather than off whatever happened since the last one.
    inFlight.current ??= { data: payloadUpdatedAt, error: payloadErroredAt };
  } else if (inFlight.current) {
    const started = inFlight.current;
    inFlight.current = null;
    if (
      payloadUpdatedAt !== started.data &&
      payloadErroredAt === started.error
    ) {
      known.current = new Map();
    }
  }

  recipients?.forEach((recipient) => {
    const answer = recipient.pack_approval ?? null;
    const signature = answerSignature(answer);
    if (fromPayload.current.get(recipient.technician_id) === signature) return;

    fromPayload.current.set(recipient.technician_id, signature);
    if (answer?.current === null) return;

    known.current.delete(recipient.technician_id);
  });

  useEffect(() => {
    polled?.pack_approvals.forEach(({ technician_id, ...approval }) => {
      if (approval.current === null) return;

      known.current.set(technician_id, approval);
    });
  }, [polled]);

  return known.current;
};
